import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import Loader from '@/common/components/Loader'
import { Header, PageTitle } from '@/components'
import { useCheckPermission } from '@/core/hooks/use-check-permission'
import { BadgeDto, RoadmapDto } from '@/common/models/gamification'
import {
  getBadges,
  getRoadmap,
  getRoadmaps,
} from '@/core/services/gamification'

import { MessageBox } from '../messages/MessageBox'
import { RankList } from './builder/RankList'
import { StepList } from './builder/StepList'
import { translationTitle } from './shared/labels'

export const RoadmapBuilderPage: React.FC = () => {
  const { festivalName, editionId, roadmapId } = useParams()
  const navigate = useNavigate()
  const { hasEditPermission } = useCheckPermission({ page: 'GAMIFICATION' })

  const [roadmap, setRoadmap] = useState<RoadmapDto | null>(null)
  const [allRoadmaps, setAllRoadmaps] = useState<RoadmapDto[]>([])
  const [badges, setBadges] = useState<BadgeDto[]>([])
  const [loading, setLoading] = useState(false)

  const [isError, setIsError] = useState(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>()
  const [showMessageBox, setShowMessageBox] = useState(false)
  const [message, setMessage] = useState('')

  const handleError = useCallback((e: unknown) => {
    setIsError(true)
    setShowMessageBox(true)
    setMessage(e instanceof Error ? e.message : 'Something went wrong')
    setErrorStatus((e as { response?: { status?: number } })?.response?.status)
  }, [])

  const refetch = useCallback(async () => {
    if (!roadmapId) return
    try {
      setLoading(true)
      const roadmapRes = await getRoadmap(roadmapId)
      setRoadmap(roadmapRes.data)
    } catch (e) {
      handleError(e)
    } finally {
      setLoading(false)
    }
  }, [roadmapId, handleError])

  const loadReferences = useCallback(async () => {
    if (!editionId) return
    try {
      const [roadmapsRes, badgesRes] = await Promise.all([
        getRoadmaps(editionId),
        getBadges(),
      ])
      setAllRoadmaps(roadmapsRes.data ?? [])
      setBadges(badgesRes.data ?? [])
    } catch (e) {
      handleError(e)
    }
  }, [editionId, handleError])

  useEffect(() => {
    refetch()
    loadReferences()
  }, [refetch, loadReferences])

  const title = roadmap
    ? translationTitle(roadmap.translations, `${roadmap.scope} roadmap`)
    : 'Roadmap'

  return (
    <>
      <Header pageTitle="Gamification" />
      {loading && <Loader />}

      <PageTitle
        icon="flash"
        title={title}
        subtitle={roadmap ? `${roadmap.scope} scope` : undefined}
        backButton
        backButtonFunction={() =>
          navigate(`/gamification/roadmaps/${festivalName}/${editionId}`)
        }
        className="mb-6"
      />

      {roadmap && (
        // `body` is `overflow: hidden` app-wide (see index.css), so a page that
        // stacks multiple full-height cards must own its vertical scroll — without
        // this, a roadmap with many steps + ranks overflows the viewport with no
        // way to scroll. Offset clears the fixed header (pt-24) + the PageTitle.
        <div className="flex flex-col gap-6 max-h-[calc(100vh-220px)] overflow-y-auto pr-1">
          <StepList
            roadmap={roadmap}
            steps={roadmap.steps ?? []}
            allRoadmaps={allRoadmaps}
            badges={badges}
            hasEditPermission={hasEditPermission}
            onChanged={refetch}
            onError={handleError}
          />

          <RankList
            roadmapId={roadmap.id}
            hasEditPermission={hasEditPermission}
            onError={handleError}
          />
        </div>
      )}

      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
